/**
 * Kontakt Filter — URL-Serialisierung + Client-Side Matching (Issue #640)
 *
 * KontaktFilterState: 7 Client-seitige Kriterien + sortBy
 * Serialisierung via URLSearchParams für window.history.replaceState
 */

import type { KontaktLabel } from './kontakt-labels';

export interface KontaktFilterState {
  labels: KontaktLabel[];
  betrieb: string;
  plz: string;
  hatEmail: boolean;
  hatTelefon: boolean;
  hatAdresse: boolean;
  importquelle: string;
  sortBy: 'name' | 'firma' | 'letzte_aktivitaet' | 'umsatz_gesamt';
}

export interface KontaktMatchContext {
  email?: string | null;
  telefon?: string | null;
  mobil?: string | null;
  adresse?: string | null;
  plz?: string | null;
  labels: KontaktLabel[];
  betriebe: string[];
  quelle?: string | null;
}

export const EMPTY_KONTAKT_FILTER: KontaktFilterState = {
  labels: [],
  betrieb: '',
  plz: '',
  hatEmail: false,
  hatTelefon: false,
  hatAdresse: false,
  importquelle: '',
  sortBy: 'name',
};

export function serializeKontaktFilter(filter: KontaktFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.labels.length) params.set('labels', filter.labels.join(','));
  if (filter.betrieb) params.set('betrieb', filter.betrieb);
  if (filter.plz) params.set('plz', filter.plz);
  if (filter.hatEmail) params.set('hatEmail', 'true');
  if (filter.hatTelefon) params.set('hatTelefon', 'true');
  if (filter.hatAdresse) params.set('hatAdresse', 'true');
  if (filter.importquelle) params.set('importquelle', filter.importquelle);
  if (filter.sortBy !== 'name') params.set('sortBy', filter.sortBy);
  return params;
}

export function parseKontaktFilterFromSearch(search: string): KontaktFilterState {
  const params = new URLSearchParams(search);
  const labelsRaw = params.get('labels');
  const sortByRaw = params.get('sortBy');
  const validSortBy = ['name', 'firma', 'letzte_aktivitaet', 'umsatz_gesamt'] as const;
  return {
    labels: labelsRaw ? (labelsRaw.split(',').filter(Boolean) as KontaktLabel[]) : [],
    betrieb: params.get('betrieb') ?? '',
    plz: params.get('plz') ?? '',
    hatEmail: params.get('hatEmail') === 'true',
    hatTelefon: params.get('hatTelefon') === 'true',
    hatAdresse: params.get('hatAdresse') === 'true',
    importquelle: params.get('importquelle') ?? '',
    sortBy: validSortBy.includes(sortByRaw as any) ? (sortByRaw as KontaktFilterState['sortBy']) : 'name',
  };
}

export function countActiveKontaktFilters(filter: KontaktFilterState): number {
  let count = 0;
  if (filter.labels.length) count++;
  if (filter.betrieb) count++;
  if (filter.plz) count++;
  if (filter.hatEmail) count++;
  if (filter.hatTelefon) count++;
  if (filter.hatAdresse) count++;
  if (filter.importquelle) count++;
  return count;
}

export function isKontaktFilterEmpty(filter: KontaktFilterState): boolean {
  return countActiveKontaktFilters(filter) === 0;
}

export function matchesKontaktFilter(kontakt: KontaktMatchContext, filter: KontaktFilterState): boolean {
  if (filter.labels.length && !filter.labels.some((l) => kontakt.labels.includes(l))) return false;
  if (filter.betrieb && !kontakt.betriebe.includes(filter.betrieb)) return false;
  if (filter.plz && !kontakt.plz?.startsWith(filter.plz)) return false;
  if (filter.hatEmail && !kontakt.email) return false;
  if (filter.hatTelefon && !kontakt.telefon && !kontakt.mobil) return false;
  if (filter.hatAdresse && !kontakt.adresse) return false;
  if (filter.importquelle && kontakt.quelle !== filter.importquelle) return false;
  return true;
}
